import { useEffect } from 'react'

import { type EventCallable, type StoreWritable } from 'effector'
import { useUnit } from 'effector-react'
import { useLocation, useNavigate } from 'react-router-dom'

export function useHashTabs<TabsKeys extends string>(
  tabsKeysArray: TabsKeys[],
  tabStore: StoreWritable<TabsKeys>,
  tabChangedEvent: EventCallable<TabsKeys>,
) {
  const { hash } = useLocation()
  const stringAsHash = (value: string) => `#${value}`
  const navigate = useNavigate()
  const tab = useUnit(tabStore)
  const changeTab = useUnit(tabChangedEvent)
  const isValidTab = (value: string): value is TabsKeys =>
    tabsKeysArray.includes(value as TabsKeys)
  useEffect(() => {
    const hashValue = hash.slice(1).split('&')[0]
    if (isValidTab(hashValue) && hashValue !== tab) {
      changeTab(hashValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, changeTab])

  useEffect(() => {
    // если hash уже установлен и совпадает с нужным — не обновляем
    const desired = `#${tab}`
    if (!hash && window.location.hash !== desired) {
      void navigate(desired, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, tab])

  // Функция смены таба – обновляет стор и URL
  const setTab = (newTab: TabsKeys) => {
    if (newTab === tab) return
    changeTab(newTab)
    void navigate(stringAsHash(newTab), { replace: true })
  }

  // Хендлер смены таба
  const handleTabChange = (newTab: string) => {
    if (!isValidTab(newTab)) return
    changeTab(newTab)
    void navigate(stringAsHash(newTab), { replace: true })
  }

  return { tab, setTab, handleTabChange }
}
